import React from 'react';
import { cn } from '@/lib/utils';

/**
 * The shared centered-column wrapper, task: desktop layout, step 1 of 2
 * (2026-09-24, widened further 2026-09-24). Replaces the identical
 * `max-w-lg mx-auto` (or `max-w-2xl mx-auto`) idiom that used to be hand
 * copied onto every page and onto BottomNav, so the one thing this app has
 * never had, a desktop breakpoint, lives in exactly one place instead of
 * ~16.
 *
 * Below `lg`, this is a byte for byte no-op: `max-w-lg` (size="md", every
 * page's own previous width) or `max-w-2xl` (size="lg", Admin/Templates'
 * previous width), same as before, with no side padding added beyond
 * whatever className a caller already passes (unchanged). Mobile web and
 * the iOS app both render under `lg` on every real device either renders
 * on, so neither is affected by this at all.
 *
 * `lg:` and up now removes the width cap entirely (max-w-none) rather than
 * swapping in a wider but still fixed cap: an earlier version of this used
 * max-w-3xl/max-w-4xl, which still left a large empty margin on both sides
 * on an actual desktop monitor, just a smaller one, since any fixed rem
 * value is eventually narrower than a wide enough screen. `lg:px-8
 * xl:px-12` (added here, not per caller) is the side gutter that keeps
 * full-bleed content off the literal edge of the browser window once the
 * cap is gone.
 *
 * Width only, on purpose (task: desktop layout, step 1 of 2): no page's own
 * fields are reflowed into multiple columns here, that is a deliberate,
 * separate step once this container exists everywhere to reflow inside of.
 *
 * className is passed through `cn()` (tailwind-merge), so a caller's own
 * padding/layout classes (px-4 py-6, flex items-center gap-2, whatever a
 * given usage already had) simply extend or override this component's own,
 * exactly as if they were still on the same div. A caller's own `px-4` has
 * no `lg:`/`xl:` variant, so it never conflicts with this component's own
 * `lg:px-8 xl:px-12`; both apply, each at its own breakpoint.
 */
const SIZES = {
  // Every page that was max-w-lg (32rem / 512px) below lg; no cap at all
  // from lg upward.
  md: 'max-w-lg lg:max-w-none',
  // Admin and Templates, the two screens already max-w-2xl (42rem / 672px)
  // below lg; same "no cap" as size="md" from lg upward, since a fixed
  // width difference between the two stops meaning anything once neither
  // has one.
  lg: 'max-w-2xl lg:max-w-none',
};

export default function PageContainer({ size = 'md', className, children, ...props }) {
  return (
    <div className={cn('mx-auto w-full lg:px-8 xl:px-12', SIZES[size], className)} {...props}>
      {children}
    </div>
  );
}
